import { ISaveResult, SaveStatus } from 'app/interfaces/ISaveStatus';
import { convertObjectToString, format, stringsAreEqualCaseInsensitive } from 'app/shared/utils/string.util';

import shellapi from 'a365-shell-api';

export enum M365Stats {
    Initializing = 'Initializing',
    ConsentPending = 'ConsentPending',
    RequestingConsent = 'RequestingConsent',
    ExtractingData = 'ExtractingData',
    PersistingData = 'PersistingData',
    TenantLockboxConsentDenied = 'TenantLockboxConsentDenied'
}

function statusMappingDescription(status: string): string {
    switch (status) {
        case 'Failed':
            return stringResources.RunningPipelineFailedDesc;
        case 'Failure':
            return stringResources.PipelineFailureDesc;
        case 'Submitting':
            return stringResources.PipelineSubmittingDesc;
        case 'InProgress':
        case 'In Progress':
            return stringResources.PipelineInProgressDesc;
        case 'Succeeded':
        case 'Completed':
            return stringResources.PipelineSucceededDesc;
        case 'Skipped':
            return stringResources.PipelineSkippedDesc;
        case 'Cancelled':
        case 'Canceled':
            return stringResources.PipelineCanceledDesc; // backend API returns either Canceled or Cancelled..
        case 'Canceling':
        case 'Cancelling':
            return stringResources.PipelineCancelingDesc;
        case 'NotStarted':
            return stringResources.PipelineNotStartedDesc;
        case 'Queued':
            return stringResources.PipelineQueuedDesc;
        case 'TimedOut':
            return stringResources.PipelineTimedOutDesc;
        case 'WaitingOnDependency':
        case 'WaitingOnDependencies':
            return stringResources.PipelineWaitingOnDependency;
        case 'Yielded':
            return stringResources.PipelineYieldedDesc;
        case 'Scheduled':
            return stringResources.PipelineScheduledDesc; // used for active activities on cosmos
        case 'Stopped':
            return stringResources.PipelineStoppedDesc;
        case 'StoppedSessionTimedout':
            return stringResources.PipelineStoppedSessionDesc;
        case 'Running':
            return stringResources.PipelineRunningDesc;
        case 'Stopped (session timed out)':
            return stringResources.PipelineStoppedSessionDesc;
        default:
            return status;
    }
}

// Converts job type from English to localized string.
export function convertJobType(jobType: string | undefined): string | undefined {
    switch (jobType) {
        case 'Spark session':
            return stringResources.Monitoring_SparkApplication_Spark_Session;
        case 'Batch job':
            return stringResources.Monitoring_SparkApplication_Batch_Job;
        default:
            return jobType;
    }
}

// Converts job status from English to localized string.
export function convertStatus(status: string | M365Stats | undefined): string | undefined {
    switch (status) {
        case 'Failed':
            return stringResources.Failed;
        case 'Failure':
            return stringResources.Failure;
        case 'Submitting':
            return stringResources.Submitting;
        case 'Starting':
            return stringResources.Status_Starting;
        case 'Stopping':
            return stringResources.Stopping;
        case 'Resuming':
            return stringResources.Resuming;
        case 'Pausing':
            return stringResources.Pausing;
        case 'Paused':
            return stringResources.Paused;
        case 'InProgress':
        case 'In Progress':
            return stringResources.InProgress;
        case 'Initial':
            return stringResources.Initial;
        case 'Succeeded':
            return stringResources.Succeeded;
        case 'Skipped':
            return stringResources.Skipped;
        case 'Cancelled':
        case 'Canceled':
            return stringResources.Canceled; // backend API returns either Canceled or Cancelled..
        case 'Canceling':
        case 'Cancelling':
            return stringResources.Canceling;
        case 'NotStarted':
            return stringResources.NotStarted;
        case 'Queued':
            return stringResources.Queued;
        case 'TimedOut':
            return stringResources.TimedOut;
        case 'WaitingOnDependency':
        case 'WaitingOnDependencies':
            return stringResources.WaitingOnDependency;
        case 'Yielded':
            return stringResources.Yielded;
        case 'Scheduled':
            return stringResources.Scheduled; // used for active activities on cosmos
        case 'Stopped':
            return stringResources.Status_Stopped;
        case 'StoppedSessionTimedout':
            return stringResources.StatusStoppedSessionTimedOut;
        case 'Running':
            return stringResources.Running;
        case 'Stopped (session timed out)':
            return stringResources.StatusStoppedSessionTimedOut;
        // purview lineage status
        case 'Forbidden':
            return stringResources.Forbidden;
        case 'ConfigNotFound':
            return stringResources.ConfigNotFound;
        case 'CannotConnect':
            return stringResources.CannotConnect;
        case 'Timeout':
            return stringResources.TimedOut;
        case 'Suspended':
            return stringResources.Suspended;
        case 'Unsupported':
            return stringResources.Unsupported;
        // M365 status
        case 'Initializing':
            return stringResources.Initializing;
        case 'ConsentPending':
            return stringResources.ConsentPending;
        case 'RequestingConsent':
            return stringResources.RequestingConsent;
        case 'ExtractingData':
            return stringResources.ExtractingData;
        case 'PersistingData':
            return stringResources.PersistingData;
        case 'Completed':
            return stringResources.Completed;
        default:
            return status;
    }
}

// Converts job status from English to localized string.
export function convertStatusToDescription(status: string | undefined, entityName: string, entityType: string): string | undefined {
    if (status) {
        return format(statusMappingDescription(status), entityName, entityType);
    }
    return '';
}

// Converts localized job staus to English.
// todo, we should avoid this
export function convertReverseStatus(status: string | undefined): string | undefined {
    switch (status) {
        case 'Submitting':
            return 'Submitting';
        case stringResources.InProgress:
            return 'In Progress';
        case stringResources.Failure:
            return 'Failure';
        case stringResources.Queued:
            return 'Queued';
        case 'Success':
            return 'Success';
        case 'Error':
            return 'Error';
        case 'Cancelled':
            return 'Cancelled';
        case stringResources.Canceled:
            return 'Canceled';
        case 'Ended':
            return 'Ended';
        default:
            return status;
    }
}

export function getStatusIcon(status: string): string {
    switch (status) {
        case stringResources.Failed:
        case stringResources.Failure:
        case stringResources.Forbidden:
        case stringResources.ConfigNotFound:
        case stringResources.CannotConnect:
        case stringResources.Disconnected:
            return 'svg-i-16x16-StatusFailed';
        case stringResources.Submitting:
            return 'svg-i_submitted_16x16';
        case stringResources.Succeeded:
        case stringResources.Completed:
        case stringResources.Connected:
            return 'svg-i-16x16-StatusSuccessful';
        case stringResources.InProgress:
        case stringResources.Canceling:
        case stringResources.Running:
        case stringResources.Scheduled:
        case stringResources.Status_Starting:
        case stringResources.Stopping:
        case stringResources.Deleting:
        case stringResources.Status_Restarting:
            return 'svg-i-16x16-InProgress';
        case stringResources.Canceled:
        case stringResources.Unsupported:
            return 'svg-i_cancelled_16x16';
        case stringResources.Skipped:
            return 'svg-i-16x16-StatusSkip';
        case stringResources.TimedOut:
            return 'svg-i-16x16-Timeout';
        case stringResources.Queued:
        case stringResources.WaitingOnDependency:
        case stringResources.Yielded:
        case stringResources.Waiting:
            return 'svg-i_queued_16x16';
        case stringResources.Status_Stopped:
        case stringResources.StatusStoppedSessionTimedOut:
            return 'svg-i_stopped_16x16';
        case stringResources.Paused:
        case stringResources.Suspended:
            return 'svg-i_paused_16x16';
        case stringResources.Created:
            return 'svg-i_created_16x16';
        case stringResources.Degraded:
        case stringResources.Unknown:
            return 'svg-i-16x16-StatusWarning';
        case stringResources.IRStatus_Inactive:
            return 'svg-i_disabled_16x16';
        case stringResources.NotStarted:
            return 'svg-stopped_20_filled'
        default:
            return '';
    }
}

export function getStatusIconWithNonLocalizedStatus(status: string): string {
    const loweCaseStatus = status ? status.toLowerCase() : status;
    switch (loweCaseStatus) {
        case 'failed':
        case 'failure':
            return 'svg-i-16x16-StatusFailed';
        case 'submitting':
            return 'svg-i_submitted_16x16';
        case 'succeeded':
        case 'completed':
        case 'online':
            return 'svg-i-16x16-StatusSuccessful';
        case 'inprogress':
        case 'cancelling':
        case 'canceling':
        case 'running':
        case 'runnable':
        case 'scheduled':
        case 'starting':
        case 'stopping':
        case 'deleting':
        case 'restarting':
        case 'snapshotting':
        case 'replicating':
        case 'dropping':
        case 'creating':
        case 'scaling':
        case 'resuming':
        case 'pausing':
            return 'svg-i-16x16-InProgress';
        case 'cancelled':
        case 'canceled':
            return 'svg-i_cancelled_16x16';
        case 'skipped':
            return 'svg-i-16x16-StatusSkip';
        case 'timedout':
            return 'svg-i-16x16-Timeout';
        case 'queued':
        case 'waitingondependencies':
        case 'waiting on dependencies':
        case 'waiting on dependency':
        case 'yielded':
        case 'waiting':
            return 'svg-i_queued_16x16';
        case 'stopped':
        case 'initial':
        case 'stopped (session timed out)':
        case 'offline':
            return 'svg-i_stopped_16x16';
        case 'paused':
            return 'svg-i_paused_16x16';
        case 'created':
            return 'svg-i_created_16x16';
        case 'degraded':
        case 'throttled':
            return 'svg-i-16x16-StatusWarning';
        case 'disabled':
            return 'svg-i-disabled-16x16';
        default:
            return '';
    }
}

export function getSaveMessageErrorByStatus(statusInterface: ISaveResult, entityName: string, entityType: string, errorText?: string): string {
    switch (statusInterface.status) {
        case SaveStatus.Succeeded:
            return '';
        case SaveStatus.PermissionDeniedToPushToBranch:
            return format(stringResources.UnableToSaveBranchPermissions, entityName, entityType);
        case SaveStatus.BranchLocked:
            return format(stringResources.UnableToSaveBranchLocked, entityName, entityType);
        case SaveStatus.FileSizeLimitExceeded:
            return stringResources.Git_MaximumFileSizeExceeded;
        default:
            if (!errorText && statusInterface.error) {
                return convertObjectToString(statusInterface.error);
            }
            return errorText;
    }
}

export function getSaveStatusNotification(
    statusInterface: ISaveResult,
    entityName: string,
    entityType: string,
    isGit: boolean,
    isA365: boolean,
    isCreating: boolean,
    notificationType: shellapi.notifications.NotificationType,
    errorText?: string
): shellapi.notifications.INotificationCreateParameter {
    let detail: string;
    let title: string;
    let subtitle: string;
    let status: shellapi.notifications.NotificationStatus;

    if (statusInterface) {
        if (statusInterface.status === SaveStatus.Succeeded) {
            status = 'Succeeded';
            /**
             * LIVE Mode (only implicit publish reaches this function)
             *   create - successfully created
             *   apply - successfully applied settings
             *
             * GIT Mode
             *   (Synapse) Commit - successfully committed
             *   (ADF) Save - successfully saved
             */
            title = isGit ? (isA365 ? stringResources.A365_Save_Success_Title : stringResources.Save_Success_Title) :
                (isCreating ? stringResources.ADF_Save_LiveCreate_Success_Title : stringResources.ADF_Save_LiveUpdate_Success_Title);
            const tmpSubtitle = isGit ? (isA365 ? stringResources.A365_Save_Success_Desc : stringResources.ADF_Save_Git_Success_Desc) :
                (isCreating ? stringResources.ADF_Save_LiveCreate_Success_Desc : stringResources.ADF_Save_LiveUpdate_Success_Desc);
            subtitle = format(tmpSubtitle, entityName, entityType);
        } else {
            status = 'Failed';
            detail = getSaveMessageErrorByStatus(statusInterface, entityName, entityType, errorText);
            title = isGit ? (isA365 ? stringResources.A365_Save_Failed_Title : stringResources.ADF_Save_Git_Failed_Title) :
                (isA365 ? stringResources.A365_Save_Failed_Title :
                    isCreating ? stringResources.ADF_Save_LiveCreate_Failed_Title : stringResources.ADF_Save_LiveUpdate_Failed_Title);
            const tmpSubtitle = isGit ? (isA365 ? stringResources.A365_Save_Failed_Desc : stringResources.ADF_Save_Git_Failed_Desc) :
                (isA365 ? stringResources.A365_Save_Failed_Desc :
                    isCreating ? stringResources.ADF_Save_LiveCreate_Failed_Title : stringResources.ADF_Save_LiveUpdate_Failed_Title);
            subtitle = format(tmpSubtitle, entityName, entityType);
        }
    }

    return {
        title,
        status,
        detail,
        type: notificationType,
        subTitle: subtitle,
        detailTitle: detail ? stringResources.Monitoring_ErrorDetailsTitle : undefined,
        actionLinkTitle: detail ? stringResources.ViewDetails : undefined
    };
}
export function getSaveStatusMessage(statusInterface: ISaveResult, entityName: string, errorText: string = ''): string {
    // deprecated for getSaveStatusNotification
    switch (statusInterface.status) {
        case SaveStatus.Succeeded:
            return format(stringResources.Resource_Saved, entityName);
        case SaveStatus.PermissionDeniedToPushToBranch:
            return format(stringResources.Resource_Save_Failed_WithError, entityName, stringResources.PermissionDeniedToPushToBranchText);
        case SaveStatus.BranchLocked:
            return format(stringResources.Resource_Save_Failed_WithError, entityName, stringResources.BranchIsLockedText);
        case SaveStatus.FileSizeLimitExceeded:
            return format(stringResources.Resource_Save_Failed_WithError, entityName, stringResources.Git_MaximumFileSizeExceeded);
        default:
            if (errorText) {
                return format(stringResources.Resource_Save_Failed_WithError, entityName, errorText);
            } else if (statusInterface.error) {
                const error = convertObjectToString(statusInterface.error);
                return format(stringResources.Resource_Save_Failed_WithError, entityName, error);
            }
            return format(stringResources.Resource_Save_Failed, entityName);
    }
}

export function getRemoveStatusNotification(
    saveStatus: SaveStatus,
    resourceType: string,
    entityName: string,
    notificationType: shellapi.notifications.NotificationType,
    isGitMode: boolean,
    isResourceSavedOrPublished: boolean,
    isFolder: boolean = false
): shellapi.notifications.INotificationCreateParameter {
    let toastTitle: string;
    let toastDescription: string;

    resourceType = isFolder ? `${resourceType} ${stringResources.Folder.toLocaleLowerCase()}` : resourceType;
    switch (saveStatus) {
        case SaveStatus.Succeeded:
            toastTitle = isResourceSavedOrPublished && !isGitMode ? stringResources.Resource_ToBeDeletedTitle :
                stringResources.Resource_Deleted_Success_Title;
            toastDescription = isResourceSavedOrPublished
                ? (isGitMode
                    ? format(stringResources.Resource_Deleted_Success_Git_Desc, entityName, resourceType, stringResources.GitRepository)
                    : format(stringResources.Resource_Publishing_Delete, entityName, resourceType))
                : format(stringResources.Resource_Deleted_Success_Desc, entityName, resourceType);
            break;
        case SaveStatus.PermissionDeniedToPushToBranch:
            toastTitle = stringResources.Resource_Deleted_Failed_Title;
            toastDescription = format(stringResources.Resource_Delete_Failed_Desc_Git_Permissions, entityName, resourceType);
            break;
        case SaveStatus.BranchLocked:
            toastTitle = stringResources.Resource_Deleted_Failed_Title;
            toastDescription = format(stringResources.Resource_Delete_Failed_Desc_Git_Branch_Locked, entityName, resourceType);
            break;
        default:
            toastTitle = stringResources.Resource_Deleted_Failed_Title;
            toastDescription = format(stringResources.Resource_Deleted_Failed_Desc, entityName, resourceType);
    }

    return {
        type: notificationType,
        title: toastTitle,
        subTitle: toastDescription,
        status: saveStatus === SaveStatus.Succeeded ? 'Succeeded' : 'Failed'
    };
}

export function getRemoveStatusMessage(statusInterface: ISaveResult, entityName: string, isDirectDelete: boolean, deleted: boolean = true, errorText: string = ''): string {
    // deprecated for getRemoveStatusNotification
    // deleted boolean value determines whether to show that the resource was deleted or disassociated
    const template = deleted ? (isDirectDelete ? stringResources.Resource_Deleted : stringResources.Resource_ToBeDeleted) : stringResources.Resource_Disassociated;
    const templateFailed = deleted ? stringResources.Resource_Delete_Failed : stringResources.Resource_Disassociate_Failed;
    const templateWithError = deleted ? stringResources.Resource_Delete_Failed_WithError : stringResources.Resource_Disassociate_Failed_WithError;
    switch (statusInterface.status) {
        case SaveStatus.Succeeded:
            return format(template, entityName);
        case SaveStatus.PermissionDeniedToPushToBranch:
            return format(templateWithError, entityName, stringResources.PermissionDeniedToPushToBranchText);
        case SaveStatus.BranchLocked:
            return format(templateWithError, entityName, stringResources.BranchIsLockedText);
        default:
            if (errorText) {
                return format(templateWithError, entityName, errorText);
            } else if (statusInterface.error) {
                const error = convertObjectToString(statusInterface.error);
                return format(templateWithError, entityName, error);
            }
            return format(templateFailed, entityName);
    }
}

export function getJobStatusFromScopeSingleJob(state?: string, result?: string): string {
    let finalState: string;
    if (!!state && !!result) {
        if (state === 'Ended') {
            finalState = result; // Succeeded, Cancelled or Failure
        } else if (state === 'In Progress' || state === 'Running' || state === 'Ending') {
            finalState = 'In Progress';
        } else if (state === 'Initializing' || state === 'Compiling' || state === 'Queued' || state === 'Scheduling') {
            finalState = 'Queued';
        }
    }
    return convertStatus(finalState);
}

export function getIconFromScopeSingleJobState(state?: string): string {
    if (state === 'Error') {
        return 'svg-i-16x16-StatusFailed';
    }
    return 'svg-i-16x16-StatusWarning';
}

export function hasCompletedStatus(objectWithStatus: {status?: string}): boolean {
    return (
        objectWithStatus &&
        !stringsAreEqualCaseInsensitive(objectWithStatus.status, 'InProgress') &&
        !stringsAreEqualCaseInsensitive(objectWithStatus.status, 'Queued') &&
        !stringsAreEqualCaseInsensitive(objectWithStatus.status, 'Cancelling') &&
        !stringsAreEqualCaseInsensitive(objectWithStatus.status, 'Canceling') &&
        !stringsAreEqualCaseInsensitive(objectWithStatus.status, stringResources.InProgress) &&
        !stringsAreEqualCaseInsensitive(objectWithStatus.status, stringResources.Queued) &&
        !stringsAreEqualCaseInsensitive(objectWithStatus.status, stringResources.Canceling)
    );
}
